import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { OcrBand, RecommendationFilter } from '@/hooks/useDviLearningDashboard';

export interface LearningFilters {
  rangeDays: number;
  stage: string; // 'all' | stage
  recommendation: RecommendationFilter;
  ocrBand: OcrBand;
  minSamples: number;
  search: string;
}

interface Props {
  value: LearningFilters;
  onChange: (next: LearningFilters) => void;
}

export function LearningFilterBar({ value, onChange }: Props) {
  const set = (patch: Partial<LearningFilters>) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-card p-4">
      <div>
        <Label className="text-xs text-muted-foreground">Date Range</Label>
        <Select value={String(value.rangeDays)} onValueChange={(v) => set({ rangeDays: Number(v) })}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Stage</Label>
        <Select value={value.stage} onValueChange={(v) => set({ stage: v })}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            <SelectItem value="none">Insufficient</SelectItem>
            <SelectItem value="suggestion">Suggestion</SelectItem>
            <SelectItem value="prefill">Prefill</SelectItem>
            <SelectItem value="job_suggestion">Job Suggestion</SelectItem>
            <SelectItem value="automation">Automation</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Min Samples</Label>
        <Select value={String(value.minSamples)} onValueChange={(v) => set({ minSamples: Number(v) })}>
          <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any</SelectItem>
            <SelectItem value="10">≥ 10</SelectItem>
            <SelectItem value="25">≥ 25</SelectItem>
            <SelectItem value="50">≥ 50</SelectItem>
            <SelectItem value="100">≥ 100</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Recommendation</Label>
        <ToggleGroup
          type="single"
          value={value.recommendation ?? 'all'}
          onValueChange={(v) => set({ recommendation: (v === 'all' || !v) ? null : (v as RecommendationFilter) })}
          className="border border-border rounded-md"
        >
          <ToggleGroupItem value="all" size="sm">All</ToggleGroupItem>
          <ToggleGroupItem value="recommended" size="sm">Rec only</ToggleGroupItem>
          <ToggleGroupItem value="not_recommended" size="sm">Not rec</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">OCR Confidence</Label>
        <ToggleGroup
          type="single"
          value={value.ocrBand ?? 'all'}
          onValueChange={(v) => set({ ocrBand: (v === 'all' || !v) ? null : (v as OcrBand) })}
          className="border border-border rounded-md"
        >
          <ToggleGroupItem value="all" size="sm">All</ToggleGroupItem>
          <ToggleGroupItem value="low" size="sm">Low</ToggleGroupItem>
          <ToggleGroupItem value="mid" size="sm">Mid</ToggleGroupItem>
          <ToggleGroupItem value="high" size="sm">High</ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}
